/**
 * V4ValidationPanel — Phase 5e-2.
 *
 * Two surfaces for surfacing validateWorkflow() output:
 *
 *   • <V4ValidationBanner>   — compact bar that sits below the workflow
 *                              header showing aggregate severity counts.
 *                              Click to toggle the panel open.
 *   • <V4ValidationPanel>    — inline expandable panel that lists every
 *                              issue grouped by severity with Jump-to-node
 *                              buttons. Doesn't push canvas content — floats
 *                              at fixed position so the operator can keep
 *                              the canvas visible while triaging.
 *
 * Both components are dumb about state: parent owns the {issues, stats}
 * value (computed via useMemo from v4Validate.js) + the expanded boolean.
 */

import React, { useState, useMemo } from 'react';
import {
  AlertOctagon, AlertTriangle, Info, ChevronDown, ChevronRight,
  X, CornerDownRight,
} from 'lucide-react';

/* Severity → visual treatment. Matches the platform's Pill tones. */
const SEVERITY_META = {
  error:   { tone: '#b91c1c', bg: '#fef2f2', label: 'Error',   icon: AlertOctagon },
  warning: { tone: '#b45309', bg: '#fffbeb', label: 'Warning', icon: AlertTriangle },
  info:    { tone: '#1e40af', bg: '#eff6ff', label: 'Info',    icon: Info },
};

/* ─────────────────────────────────────────────────────────────────────
 * V4ValidationBanner — compact summary chip strip below the header.
 *
 * Renders nothing when there are zero issues — clean canvas means no
 * banner noise. When issues exist, shows a one-row strip with severity
 * counts + "Show / Hide details" toggle.
 * ─────────────────────────────────────────────────────────────────── */
export function V4ValidationBanner({ stats, expanded, onToggle }) {
  if (!stats || stats.total === 0) return null;
  const hasErrors = stats.error > 0;
  return (
    <div
      role="region"
      aria-label="Workflow validation summary"
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '8px 20px',
        background: hasErrors ? '#fef2f2' : '#fffbeb',
        borderBottom: hasErrors ? '1px solid #fecaca' : '1px solid #fcd34d',
        fontSize: 12, color: hasErrors ? '#7f1d1d' : '#78350f',
        flexShrink: 0,
      }}
    >
      {hasErrors
        ? <AlertOctagon size={16} style={{ color: '#dc2626', flexShrink: 0 }} aria-hidden="true" />
        : <AlertTriangle size={16} style={{ color: '#d97706', flexShrink: 0 }} aria-hidden="true" />}

      <span style={{ fontWeight: 600 }}>
        Workflow has{' '}
        {stats.error > 0   && <SeverityCount kind="error"   n={stats.error} />}
        {stats.warning > 0 && (stats.error > 0 ? ', ' : '')}
        {stats.warning > 0 && <SeverityCount kind="warning" n={stats.warning} />}
        {stats.info > 0    && ((stats.error + stats.warning) > 0 ? ', ' : '')}
        {stats.info > 0    && <SeverityCount kind="info"    n={stats.info} />}
        .
      </span>

      <span style={{ flex: 1 }} />

      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 9px', fontSize: 11.5, fontWeight: 600,
          color: hasErrors ? '#7f1d1d' : '#78350f',
          background: hasErrors ? 'rgba(220, 38, 38, 0.08)' : 'rgba(217, 119, 6, 0.08)',
          border: '0.5px solid currentColor',
          borderRadius: 4,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {expanded
          ? <ChevronDown size={12} aria-hidden="true" />
          : <ChevronRight size={12} aria-hidden="true" />}
        {expanded ? 'Hide details' : 'Show details'}
      </button>
    </div>
  );
}

/* Inline-text count chip. e.g. "3 errors" with the right color word. */
function SeverityCount({ kind, n }) {
  const meta = SEVERITY_META[kind] || SEVERITY_META.info;
  const label = n === 1 ? meta.label.toLowerCase() : `${meta.label.toLowerCase()}s`;
  return (
    <span style={{ color: meta.tone, fontWeight: 700 }}>
      {n} {label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * V4ValidationPanel — expandable list of issues, one row per issue.
 *
 * Position: absolute, anchored top-right of the canvas surface, ~360px
 * wide. Floats above the canvas; the operator can still see node
 * positions while clicking through issues. Clicking an issue invokes
 * onJumpToNode(nodeId) which the host wires to select + scroll-into-view.
 *
 * Grouping: by severity (errors first, then warnings, then info).
 * Within each group, by code, then by message order.
 * ─────────────────────────────────────────────────────────────────── */
export default function V4ValidationPanel({
  issues,
  onJumpToNode,
  onClose,
  /* Phase 7c-3 — strip floating chrome when rendered inside V4RightRail. */
  embedded = false,
}) {
  const grouped = useMemo(() => {
    const byKind = { error: [], warning: [], info: [] };
    (issues || []).forEach((i) => {
      if (byKind[i.severity]) byKind[i.severity].push(i);
    });
    return byKind;
  }, [issues]);

  const total = (issues || []).length;

  return (
    <aside
      role="region"
      aria-label="Workflow validation issues"
      style={embedded ? {
        width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column',
        background: '#ffffff',
        fontFamily: 'inherit',
        overflow: 'hidden',
      } : {
        position: 'absolute',
        top: 12,
        right: 12,
        width: 380,
        maxHeight: 'calc(100% - 24px)',
        display: 'flex', flexDirection: 'column',
        background: '#ffffff',
        border: '0.5px solid #e2e8f0',
        borderRadius: 8,
        boxShadow: '0 14px 36px -10px rgba(15, 23, 42, 0.22), 0 3px 8px -2px rgba(15, 23, 42, 0.10)',
        zIndex: 8,
        fontFamily: 'inherit',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px',
        borderBottom: '0.5px solid #e2e8f0',
      }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: '#0f172a',
                       textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Validation
        </span>
        <span style={{ fontSize: 10.5, color: '#64748b' }}>
          {total} issue{total === 1 ? '' : 's'}
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onClose}
          aria-label="Hide validation panel"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 22, height: 22, padding: 0,
            background: '#f1f5f9', border: '0.5px solid #e2e8f0',
            borderRadius: 4, color: '#475569', cursor: 'pointer',
          }}
        >
          <X size={12} aria-hidden="true" />
        </button>
      </div>

      {/* Body */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {total === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
            No issues — workflow looks clean.
          </div>
        )}
        {['error', 'warning', 'info'].map((kind) => {
          const list = grouped[kind];
          if (!list || list.length === 0) return null;
          return (
            <SeverityGroup
              key={kind}
              kind={kind}
              issues={list}
              onJumpToNode={onJumpToNode}
            />
          );
        })}
      </div>
    </aside>
  );
}

/* One severity section — header + per-issue rows. */
function SeverityGroup({ kind, issues, onJumpToNode }) {
  const meta = SEVERITY_META[kind] || SEVERITY_META.info;
  const Icon = meta.icon;
  return (
    <section>
      <header style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 12px',
        background: meta.bg,
        borderBottom: '0.5px solid #e2e8f0',
      }}>
        <Icon size={11} style={{ color: meta.tone }} aria-hidden="true" />
        <span style={{
          fontSize: 10.5, fontWeight: 700, color: meta.tone,
          textTransform: 'uppercase', letterSpacing: 0.4,
        }}>
          {meta.label} ({issues.length})
        </span>
      </header>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {issues.map((issue) => (
          <IssueRow key={issue.id} issue={issue} onJumpToNode={onJumpToNode} />
        ))}
      </ul>
    </section>
  );
}

/* Single-issue row. Click → onJumpToNode(nodeId) when issue has a target. */
function IssueRow({ issue, onJumpToNode }) {
  const canJump = !!issue.nodeId && typeof onJumpToNode === 'function';
  const RowEl = canJump ? 'button' : 'div';
  const meta = SEVERITY_META[issue.severity] || SEVERITY_META.info;
  return (
    <li style={{ borderBottom: '0.5px solid #f1f5f9' }}>
      <RowEl
        type={canJump ? 'button' : undefined}
        onClick={canJump ? () => onJumpToNode(issue.nodeId) : undefined}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          width: '100%', padding: '8px 12px',
          background: '#ffffff',
          border: 'none', textAlign: 'left',
          cursor: canJump ? 'pointer' : 'default',
          fontFamily: 'inherit',
        }}
      >
        <span
          style={{
            display: 'inline-block', flexShrink: 0,
            width: 4, height: 4, marginTop: 6,
            borderRadius: 999,
            background: meta.tone,
          }}
          aria-hidden="true"
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: '#0f172a', lineHeight: 1.45 }}>
            {issue.message}
          </div>
          {issue.suggest && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 4,
              marginTop: 3,
              fontSize: 10.5, color: '#475569',
            }}>
              <CornerDownRight size={10} style={{ marginTop: 2, flexShrink: 0, color: '#94a3b8' }} aria-hidden="true" />
              <span>{issue.suggest}</span>
            </div>
          )}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            marginTop: 4, fontSize: 9.5, color: '#94a3b8',
          }}>
            <code style={{
              padding: '0 4px', borderRadius: 3,
              background: '#f1f5f9', color: '#475569',
              fontFamily: 'SFMono-Regular, Menlo, monospace',
              fontSize: 9,
            }}>
              {issue.code}
            </code>
            {canJump && <span style={{ color: '#0ea5e9' }}>Jump to node ↗</span>}
          </div>
        </div>
      </RowEl>
    </li>
  );
}
